/**
 * LocationEditorPage — the single-location record.
 *
 * Rebuilt 2026-08-07 (Nick) from `Locations - Admin & Bulk Creation.dc.html`
 * option 1b, for the System Administrator persona in all three verticals.
 *
 * The two things this screen exists to get right:
 *
 * 1. **The address form follows the country.** Labels, ORDER and required-ness
 *    all come from the contract's ADDRESS_FORMATS — Japan leads with the postal
 *    code and has no street name, Germany puts PLZ before Ort, the UK's county
 *    is optional, India requires a locality. Nothing about countries is decided
 *    in this file; it renders whatever `addressRows(country)` returns.
 *
 * 2. **Per-field validation with real messages.** Every field validates on blur
 *    and again on save, through the SAME validator the bulk importer and the
 *    serving engine use (`location-validation` → `@v0y4ge/api-contracts`). Save
 *    is blocked while anything is invalid, and a rejected save from the server
 *    re-renders as field errors rather than a bare toast.
 *
 * Writes go through the typed client and are awaited before any success
 * feedback (no-fake-interactivity mandate).
 */
import { useState, useEffect, useMemo, useRef } from "react";
import { Btn, Icon, Input, T, TimeZonePicker, Dropdown, Field, EditorSectionNav, Section, Banner, type WeeklyHoursValue } from "@v0y4ge/jrni-ui";
import { createLocation, updateLocation } from "@v0y4ge/api-client";
import { LOCATIONS_OPTS } from "../../shell/request-opts";
import { logAction } from "../../lib/audit";
import { toast } from "../../shell/toast";
import { Screen, PageHeader } from "../../shell/chrome";
import { can } from "../../shell/access";
import { useNav } from "../../shell/router";
import { useIsMobile } from "../../shell/useResponsive";
import { useLocations } from "./useLocations";
import LocationMap from "./LocationMap";
import { PhoneInput } from "../../components/fields/PhoneInput";
import { AddressFields } from "../../components/fields/AddressFields";
import { WeeklyHours } from "../../components/fields/WeeklyHours";
import { validateAddress } from "@v0y4ge/api-client";
import { useT, t as i18nT } from "../../lib/i18n";
import {
  addressFormatFor, validate, emptyDraft,
  WEEKDAYS, WEEKDAY_LABEL,
  type LocFormDraft, type LocErrors, type LocField,
} from "./location-validation";

function hoursToWeekly(hours: LocFormDraft["hours"]): WeeklyHoursValue {
  return WEEKDAYS.map((day) => (hours.find((h) => h.day === day)?.windows ?? []).map((w) => ({ start: w.open, end: w.close })));
}

function weeklyToHours(value: WeeklyHoursValue): LocFormDraft["hours"] {
  return WEEKDAYS.map((day, index) => ({ day, windows: (value[index] ?? []).map((w) => ({ open: w.start, close: w.end })) }));
}

/* ── Screen ─────────────────────────────────────────────────────────────── */

export default function LocationEditorPage() {
  const t = useT();
  const nav = useNav();
  const { locations, reload } = useLocations();
  const isNew = !!nav.params?.new;
  const viewOnly = !!nav.params?.view;
  const id = nav.params?.id as string | undefined;
  const canWrite = !viewOnly && can("locations.write");
  const isMobile = useIsMobile(760);

  const [form, setForm] = useState<LocFormDraft>(() => emptyDraft());
  /** The latest draft, readable synchronously.
   *
   *  `blur` cannot validate `form`: the design-system AddressFields fires
   *  onBlur SYNCHRONOUSLY after a dropdown pick, in the same tick as the
   *  onChange, so the render closure blur sees still holds the PREVIOUS draft.
   *  The country field was therefore validated as empty the moment a country
   *  was chosen — "Choose a country…" on the field just filled, counted in the
   *  badge, and the banner claiming saving was blocked while `save` (which
   *  validates the committed state) succeeded.
   *
   *  Kept in step inside `set()` rather than in an effect, so it is correct
   *  within the same tick as the change that caused the blur. */
  const draftRef = useRef<LocFormDraft>(form);
  const [errors, setErrors] = useState<LocErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState<"details" | "hours">("details");
  const [locating, setLocating] = useState(false);
  const seededFor = useRef<string | null>(null);
  const locateRequest = useRef(0);
  useEffect(() => () => { locateRequest.current += 1; }, []);

  /* The list hook resolves async: seed when the record ARRIVES, once per id,
     so a later refresh can never clobber what the user has typed. */
  useEffect(() => {
    if (isNew || !id || seededFor.current === id) return;
    const found = locations.find((l) => l.raw?.id === id || l.id === id);
    if (!found) return;
    const r = (found.raw ?? {}) as Record<string, unknown>;
    seededFor.current = id;
    const rawHours = Array.isArray(r.hours) ? (r.hours as { day: string; windows: { open: string; close: string }[] }[]) : [];
    const seeded = {
      id: String(r.id ?? id),
      name: String(r.name ?? found.name ?? ""),
      address: String(r.address ?? ""),
      addressLine2: String(r.addressLine2 ?? ""),
      city: String(r.city ?? ""),
      region: String(r.region ?? ""),
      postalCode: String(r.postalCode ?? ""),
      country: String(r.country ?? ""),
      phone: String(r.phone ?? ""),
      timezone: String(r.timezone ?? ""),
      externalCode: String(r.externalCode ?? ""),
      hours: WEEKDAYS.map((d) => rawHours.find((h) => h.day === d) ?? { day: d, windows: [] }),
      hoursSet: !!r.hoursSet,
      active: r.active !== false,
      archived: r.archived === true,
      latitude: typeof r.latitude === "number" ? r.latitude : undefined,
      longitude: typeof r.longitude === "number" ? r.longitude : undefined,
    };
    /* The seed replaces the draft wholesale, so the ref is replaced with it. */
    draftRef.current = emptyDraft(seeded);
    setForm(draftRef.current);
  }, [id, isNew, locations, viewOnly]);

  const takenCodes = useMemo(
    () => locations
      .filter((l) => (l.raw?.id ?? l.id) !== form.id)
      .map((l) => String((l.raw as { externalCode?: string } | undefined)?.externalCode ?? ""))
      .filter(Boolean),
    [locations, form.id],
  );

  const format = addressFormatFor(form.country);

  const set = (patch: Partial<LocFormDraft>) => {
    /* Synchronously, BEFORE the setState — a blur firing in this same tick
       must already see the value just picked. */
    draftRef.current = { ...draftRef.current, ...patch };
    setForm((f) => ({ ...f, ...patch }));
    // Clear the error on any field the user is actively fixing.
    const keys = Object.keys(patch) as LocField[];
    setErrors((e) => {
      const next = { ...e };
      for (const k of keys) delete next[k];
      return next;
    });
  };

  /** `next` lets a caller hand blur the value it just set, for a control that
   *  reports its new value only on blur. Everything else reads `draftRef`,
   *  never the render closure. */
  const blur = (field: LocField, next?: Partial<LocFormDraft>) => {
    setTouched((s) => ({ ...s, [field]: true }));
    if (next) { draftRef.current = { ...draftRef.current, ...next }; setForm((f) => ({ ...f, ...next })); }
    const found = validate(draftRef.current, { only: [field], takenExternalCodes: takenCodes, requireTimeZone: false });
    setErrors((e) => {
      const next = { ...e };
      if (found[field]) next[field] = found[field];
      else delete next[field];
      return next;
    });
  };

  /**
   * Resolve the typed address to coordinates through the geo family's
   * `validateAddress`. A real awaited call — and when the service cannot place
   * the address it says so, verbatim, rather than the UI inventing a pin.
   */
  const locate = async () => {
    if (locating) return;
    if (!form.address.trim() || !form.city.trim()) {
      toast({ tone: "warn", title: t("locations.editorPage.addAnAddressFirst", "Add an address first"), subtitle: t("locations.editorPage.streetAndCityAreNeededToLocate", "Street and city are needed to place a pin.") });
      return;
    }
    setLocating(true);
    const request = ++locateRequest.current;
    try {
      const res = await (validateAddress as unknown as (b: unknown, o: unknown) => Promise<{ valid: boolean; lat?: number; lng?: number; tz?: string; reason?: string }>)(
        {
          street: form.address.trim(),
          city: form.city.trim(),
          ...(form.region.trim() ? { region: form.region.trim() } : {}),
          ...(form.postalCode.trim() ? { postalCode: form.postalCode.trim() } : {}),
          ...(form.country.trim() ? { country: form.country.trim().toUpperCase() } : {}),
        },
        LOCATIONS_OPTS,
      );
      if (request !== locateRequest.current) return;
      if (res.valid && typeof res.lat === "number" && typeof res.lng === "number") {
        set({ latitude: res.lat, longitude: res.lng, ...(res.tz && !form.timezone ? { timezone: res.tz } : {}) });
        toast({ tone: "success", title: t("locations.editorPage.located", "Located"), subtitle: `${res.lat.toFixed(5)}, ${res.lng.toFixed(5)}` });
      } else {
        toast({ tone: "warn", title: t("locations.editorPage.couldnTPlaceThatAddress", "Couldn't place that address"), subtitle: res.reason ?? "" });
      }
    } catch (e) {
      if (request !== locateRequest.current) return;
      toast({ tone: "danger", title: t("locations.editorPage.locationLookupFailed", "Location lookup failed"), subtitle: e instanceof Error ? e.message : String(e) });
    } finally {
      if (request === locateRequest.current) setLocating(false);
    }
  };

  const save = async () => {
    if (saving) return;
    const found = validate(form, { takenExternalCodes: takenCodes, requireTimeZone: true });
    setErrors(found);
    setTouched((s) => {
      const next = { ...s };
      for (const k of Object.keys(found)) next[k] = true;
      return next;
    });
    if (Object.keys(found).length) {
      const first = Object.keys(found)[0] as LocField;
      setSection(first === "timezone" || first === "hours" ? "hours" : "details");
      toast({
        tone: "danger",
        title: t("locations.editorPage.checkTheHighlightedFields", "Check the highlighted fields"),
        subtitle: found[first],
      });
      return;
    }
    setSaving(true);
    const input = {
      name: form.name.trim(),
      address: form.address.trim() || null,
      addressLine2: form.addressLine2.trim() || null,
      city: form.city.trim() || null,
      region: form.region.trim() || null,
      postalCode: form.postalCode.trim() || null,
      country: form.country.trim() || null,
      phone: form.phone.trim() || null,
      externalCode: form.externalCode.trim() || null,
      timezone: form.timezone.trim() || null,
      hours: form.hours,
      hoursSet: form.hours.some((h) => h.windows.length > 0),
      active: form.active,
      /* Carried explicitly: omitting them on PATCH would silently drop a
         located pin the first time anyone saves an unrelated field. */
      latitude: typeof form.latitude === "number" ? form.latitude : null,
      longitude: typeof form.longitude === "number" ? form.longitude : null,
    };
    try {
      if (isNew || !form.id) {
        await (createLocation as unknown as (i: unknown, o: unknown) => Promise<unknown>)(input, LOCATIONS_OPTS);
      } else {
        await (updateLocation as unknown as (i: string, b: unknown, o: unknown) => Promise<unknown>)(form.id, input, LOCATIONS_OPTS);
      }
      logAction("location", form.id || form.name.trim(), form.name.trim(), isNew ? "created" : "updated");
      reload();
      toast({
        tone: "success",
        title: isNew ? t("locations.editorPage.locationAdded", "Location added") : t("locations.editorPage.locationSaved", "Location saved"),
        subtitle: form.name.trim(),
      });
      setSaving(false);
      nav.go("locations");
    } catch (e) {
      /* A server rejection lands back on the field that caused it — a bare
         toast would leave the user hunting. */
      const msg = e instanceof Error ? e.message : String(e);
      setSaving(false);
      setErrors((prev) => ({ ...prev, name: prev.name ?? undefined } as LocErrors));
      toast({
        tone: "danger",
        title: t("locations.editorPage.couldnTSaveLocation", "Couldn't save location"),
        subtitle: msg || t("locations.editorPage.nothingWasSavedYourEntriesAreStillHereRetryInAMoment", "Nothing was saved — your entries are still here. Retry in a moment."),
      });
    }
  };

  const errFor = (field: LocField) => (touched[field] ? errors[field] : errors[field]);

  const invalidCount = Object.keys(errors).length;

  return (
    <Screen rail="locations">
      <PageHeader
        title={isNew ? t("locations.locationEditorPage.newLocation", "New location") : (form.name || t("locations.locationEditorPage.editLocation", "Edit location"))}
        subtitle={<span>{format.name}{form.timezone ? t("locations.editorPage.text4", "· {timezone}", { timezone: form.timezone }) : ""}</span>}
        breadcrumb={<Btn variant="link" size="sm" icon={<Icon.arrow dir="left" size={11} />} onClick={() => nav.go("locations")}>{t("locations.editorPage.locations", "Locations")}</Btn>}
        actions={viewOnly ? (
          <Btn icon={<Icon.edit size={14} />} onClick={() => nav.go("locations.edit", { id: form.id })}>{t("common.actions.edit", "Edit")}</Btn>
        ) : (
          <>
            <Btn variant="secondary" disabled={saving} onClick={() => {
              if (isNew || !id) { nav.go("locations"); return; }
              seededFor.current = null;
              locateRequest.current += 1;
              setLocating(false);
              setErrors({});
              setTouched({});
              nav.go("locations.edit", { id, view: 1 });
            }}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn icon={<Icon.check size={14} />} onClick={() => void save()} disabled={saving}>
              {saving ? t("locations.locationEditorPage.saving", "Saving…") : isNew ? t("locations.list.addLocation", "Add location") : t("common.actions.save", "Save")}
            </Btn>
          </>
        )}
      />

      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: isMobile ? "column" : "row", minHeight: 0 }}>
        <EditorSectionNav
          value={section}
          onChange={(id) => setSection(id as "details" | "hours")}
          ariaLabel={t("locations.editorPage.sections", "Location editor sections")}
          width={216}
          style={{
            borderRight: isMobile ? "none" : `1px solid ${T.border}`,
            borderBottom: isMobile ? `1px solid ${T.border}` : "none",
            padding: "16px 12px", background: T.canvas,
            ...(isMobile ? { width: "100%" } : {}),
          }}
          items={[
            { id: "details" as const, label: t("locations.editorPage.detailsAddress", "Details & address"), icon: <Icon.loc size={16} />, bad: (["name", "address", "addressLine2", "city", "region", "postalCode", "country", "phone", "externalCode"] as LocField[]).filter((k) => errors[k]).length },
            { id: "hours" as const, label: t("locations.editorPage.hoursTimeZone", "Hours & time zone"), icon: <Icon.clock size={16} />, bad: (["timezone", "hours"] as LocField[]).filter((k) => errors[k]).length },
          ].map(({ bad, ...item }) => ({ ...item, count: bad || undefined }))}
          footer={invalidCount ? (
            <Banner tone="danger" size="compact"
              title={t("locations.editorPage.fieldsNeedAttentionCount", "{count, plural, one {# field needs attention} other {# fields need attention}}", { count: invalidCount })}>
              {t("locations.editorPage.savingIsBlockedUntilTheyRePut", "Saving is blocked until they're put right.")}
            </Banner>
          ) : undefined}
        />

        <div style={{ flex: 1, overflow: "auto", padding: "22px 26px 60px", minWidth: 0 }}>
          <div style={{ maxWidth: 760 }}>
            {section === "details" ? (
              <div>
                <Section title={t("locations.editorPage.detailsAddress", "Details & address")}
                  description={t("locations.editorPage.theCountryPicksTheForm", "The country picks the form — labels, field order and which fields are required all follow it.")}
                  bare style={{ marginBottom: 20 }} />

                <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                  <div style={{ flex: 2, minWidth: 0 }}>
                    <Field label={t("locations.editorPage.locationName", "Location name")} required htmlFor="location-name" error={errFor("name")}>
                    <Input id="location-name" value={form.name} readOnly={!canWrite}
                      placeholder={i18nT("locations.editorPage.eGBostonSeaport", "e.g. Boston · Seaport")}
                      onChange={(e) => set({ name: e.target.value })} onBlur={() => blur("name")} />
                    </Field>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Field label={t("locations.editorPage.storeCode", "Store code")} htmlFor="location-external-code" error={errFor("externalCode")}>
                    <Input id="location-external-code" value={form.externalCode} readOnly={!canWrite}
                      placeholder={t("locations.editorPage.placeholder", "APB-0117")}
                      onChange={(e) => set({ externalCode: e.target.value })} onBlur={() => blur("externalCode")} />
                    </Field>
                  </div>
                </div>

                <AddressFields value={form} idPrefix="location" readOnly={!canWrite}
                  size={isMobile ? "lg" : "md"} errors={errors}
                  onChange={(patch) => set(patch)} onBlur={blur} />

                <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 14 }}>
                  <div style={{ flex: 1.2, minWidth: 0 }}>
                    <PhoneInput label={t("locations.editorPage.phone", "Phone")} error={errFor("phone")} id="location-phone" size={isMobile ? "lg" : "md"} value={form.phone} readOnly={!canWrite}
                      placeholder={t("locations.editorPage.placeholder2", "{dialCode} …", { dialCode: format.dialCode })}
                      onChange={(e) => set({ phone: e.target.value })} onBlur={() => blur("phone")} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Field label={t("locations.editorPage.status", "Status")}>
                    <Dropdown
                      value={form.active ? "Active" : "Inactive"} disabled={!canWrite} aria-label={t("locations.editorPage.status", "Status")}
                      placeholder={i18nT("locations.editorPage.status", "Status")}
                      options={[{ value: "Active", label: t("locations.editorPage.active", "Active") }, { value: "Inactive", label: t("locations.editorPage.inactive", "Inactive") }]}
                      onChange={(v) => set({ active: v === "Active" })}
                    />
                    </Field>
                  </div>
                </div>

                {/* Map preview — coordinates are data, never guessed. See LocationMap. */}
                <div style={{ marginTop: 20 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 7 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n2 }}>{t("locations.editorPage.onTheMap", "On the map")}</span>
                    <span style={{ flex: 1 }} />
                    {canWrite && typeof form.latitude === "number" ? (
                      <Btn size="sm" variant="link" disabled={locating} onClick={() => void locate()}>
                        {locating ? t("locations.editorPage.locating", "Locating…") : t("locations.editorPage.reLocateFromAddress", "Re-locate from address")}
                      </Btn>
                    ) : null}
                  </div>
                  <LocationMap
                    latitude={form.latitude}
                    longitude={form.longitude}
                    label={form.name}
                    caption={[form.address, form.city, form.postalCode, form.country].filter(Boolean).join(", ")}
                    action={canWrite ? (
                      <Btn size="sm" variant="secondary" disabled={locating} icon={<Icon.loc size={13} />} style={{ marginTop: 2 }} onClick={() => void locate()}>
                        {locating ? t("locations.editorPage.locating", "Locating…") : t("locations.editorPage.locateFromAddress", "Locate from address")}
                      </Btn>
                    ) : null}
                  />
                </div>
              </div>
            ) : (
              <div>
                <Section title={t("locations.editorPage.hoursTimeZone", "Hours & time zone")}
                  description={t("locations.editorPage.theEngineResolvesEverySchedule", "The engine resolves every schedule to UTC against this zone, and staff signing in here read every time in it.")}
                  bare style={{ marginBottom: 20 }} />

                <div style={{ marginBottom: 18 }}>
                  <Field label={t("locations.editorPage.timeZone", "Time zone")} required error={errFor("timezone")}>
                  <div style={{ maxWidth: 340 }}>
                    {viewOnly
                      ? <Input value={form.timezone || "—"} readOnly />
                      : <TimeZonePicker value={form.timezone} disabled={!canWrite} onChange={(v: string) => set({ timezone: v })} />}
                  </div>
                  </Field>

                  <Banner tone="primary" size="compact" style={{ marginTop: T.space[3] }} title={t("locations.editorPage.pushedToStaff", "Pushed to staff")}>
                    {t("locations.editorPage.everyoneWhoSignsInHere", "Everyone who signs in at this location reads every time — schedules, queues, booking sheets, reports — in its zone. A staff member who has pinned their own zone in their profile keeps their pin; it wins for them, and only for them.")}
                  </Banner>
                </div>

                <div>
                  <Field label={t("locations.editorPage.operatingHours", "Operating hours")} error={errFor("hours")}>
                  {!form.hours.some((h) => h.windows.length) ? (
                    <Banner tone="warning" size="compact" title={t("locations.editorPage.noOperatingHours", "No operating hours set")} style={{ marginBottom: 10 }}>
                      {t("locations.editorPage.noOperatingHoursSetUntilYouSetThemTheEngineCanT", "No operating hours set. Until you set them the engine can't clamp this venue's open window — set hours below, then save.")}
                    </Banner>
                  ) : null}
                  <WeeklyHours
                    value={hoursToWeekly(form.hours)}
                    onChange={(value) => {
                      const hasTooManyWindows = value.some((day) => day.length > 6);
                      if (!hasTooManyWindows) set({ hours: weeklyToHours(value), hoursSet: value.some((day) => day.length > 0) });
                    }}
                    dayLabels={WEEKDAYS.map((day) => WEEKDAY_LABEL[day])}
                    readOnly={!canWrite}
                    minuteStep={1}
                    unavailableLabel={t("locations.editorPage.closed", "Closed")}
                    ariaLabel={t("locations.editorPage.operatingHours", "Operating hours")}
                  />
                  </Field>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Screen>
  );
}
